import React, { useState, useEffect } from 'react';
import {
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover as MuiPopover,
  SxProps
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Theme } from '@mui/material/styles';

export type ActionOption = {
  label: string;
  onClick: () => void;
  itemColor?: string;
  icon?: React.ReactElement;
  sx?: SxProps;
  isLoading?: boolean;
  loadingLabel?: string;
  disabled?: boolean;
};

type ActionPopoverProps = {
  options: ActionOption[];
  id: string;
  iconSx?: SxProps;
};

// Icon styles
const iconContainerBase: SxProps<Theme> = {
  minWidth: 0,
  mr: 1
};
const clonedIconSx: SxProps<Theme> = {
  fontSize: '20px',
  width: '20px',
  height: '20px'
};
const defaultMoreIconSx: SxProps<Theme> = {
  color: 'primary.main'
};
const loadingSpinnerSx: SxProps<Theme> = {
  ml: 1,
  color: 'grey.500'
};
// Dynamic style function for icon container with custom color
const getIconContainerSx = (itemColor?: string): SxProps<Theme> => ({
  ...iconContainerBase,
  color: itemColor ?? 'primary.main'
});

const ActionPopover: React.FC<ActionPopoverProps> = ({
  options,
  id,
  iconSx
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [wasLoading, setWasLoading] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Auto-close menu when loading completes
  useEffect(() => {
    const hasLoadingOption = options.some((opt) => opt.isLoading);

    // If we were loading and now we're not, close the menu
    if (wasLoading && !hasLoadingOption && open) {
      handleClose();
    }
    // Track loading state
    setWasLoading(hasLoadingOption);
  }, [options, wasLoading, open]);

  return (
    <div>
      <IconButton onClick={handleClick} data-testid={`${id}-more-button`}>
        <MoreVertIcon sx={iconSx || defaultMoreIconSx} />
      </IconButton>
      <MuiPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <List>
          {options.map((option) => (
            <ListItemButton
              key={`${option.label}-button`}
              onClick={() => {
                if (!option.isLoading && !option.disabled) {
                  option.onClick();
                  if (!option.loadingLabel) {
                    handleClose();
                  }
                }
              }}
              disabled={option.isLoading || option.disabled}
              sx={option.sx}
            >
              {!option.isLoading && option.icon && (
                <ListItemIcon sx={getIconContainerSx(option.itemColor)}>
                  {React.cloneElement(option.icon, {
                    sx: clonedIconSx
                  })}
                </ListItemIcon>
              )}

              <ListItemText
                primary={option.isLoading ? option.loadingLabel : option.label}
                primaryTypographyProps={{
                  fontWeight: 'bold',
                  color: option.isLoading
                    ? 'grey.500'
                    : option.itemColor || 'primary.main'
                }}
              />
              {option.isLoading && (
                <CircularProgress size={16} sx={loadingSpinnerSx} />
              )}
            </ListItemButton>
          ))}
        </List>
      </MuiPopover>
    </div>
  );
};

export { ActionPopover };
